import React, { useContext, useEffect, useRef, useState } from "react";
import { Alert, Platform, StyleSheet, View } from "react-native";
import { Modal, Text, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteFactoryRunShipment,
  getChinaTrackerShipmentDetailsByDetailId,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useTranslation } from "../../../utils/useTranslation";
import { ChinaTrackerDataTableWithPagination2 } from "../../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination2";
import { useToast } from "react-native-toast-notifications";
import FactoryRunShipmentModal from "./ChinaTrackerShipmentModal";
import ChinaTrackerShipmentModalUpdate from "./ChinaTrackerShipmentModalUpdate";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
  prefixRow: any;
}

const ChinaTrackerShipmentListModal: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
  prefixRow,
}: any) => {
  const listRef = useRef<ScrollView>(null);

  const dispatch = useDispatch<any>();
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(0);

  const headers = {
    shipmentNo: { name: t("shipmentNo"), width: 1, textAlign: "left" },
    containerNo: { name: t("containerNo"), width: 1, textAlign: "left" },
    shipmentQty: { name: t("shipmentQty"), width: 1, textAlign: "left" },
    ETD: { name: t("etd"), width: 1, textAlign: "left" },
    duration: { name: t("duration"), width: 1, textAlign: "left" },
    ETA: { name: t("eta"), width: 1, textAlign: "left" },
    ...(prefixRow?.OrderStatus !== "Cancelled" && {
      shipmentActions: { name: t("actions"), width: 1, textAlign: "center" },
    }),
  };

  const { isLargeDevice, isMediumDevice } = useContext(
    DeviceOrientationContext,
  );
  const [, setContentOffSet] = useState<number>(0);

  const handleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  // Function to fetch shipments
  const handleViewShipments = async (row: any) => {
    // Call your API to fetch shipments
    dispatch(getChinaTrackerShipmentDetailsByDetailId(row._id))
      .unwrap()
      .then(() => {
        // Set shipment data
      })
      .catch((err: any) => {
        toast.show(err?.response?.data?.message || "Shipments failed to load", {
          type: "error",
        });
      });
  };

  useEffect(() => {
    let isMounted: boolean = true;

    if (order) {
      handleViewShipments(order);
    }

    return () => {
      isMounted = false;
    };
  }, [order]);

  const [modalOpen, setModalOpen] = useState<boolean>();
  const onCloseEnquiry = () => {
    setModalOpen(false);
  };

  const toast = useToast();

  const {
    orderDetailShipments: shipmentRows,
    loadingShipments,
    loadingShipmentsAction,
  } = useSelector((state: any) => state.chinaTracker);

  // Delete Shipments

  const handleDeletePress = () => {
    const lastIndexShipment = shipmentRows[shipmentRows.length - 1];
    if (Platform.OS === "web") {
      const confirmed = window.confirm(
        `Are you sure you want to delete shipmentNo: ${lastIndexShipment?.shipmentNo || "this shipment"
        }? This action cannot be undone.`,
      );
      if (confirmed) confirmDelete(lastIndexShipment);
    } else {
      Alert.alert(
        "Confirm Delete",
        `Are you sure you want to delete shipmentNo: ${lastIndexShipment?.shipmentNo || "this shipment"
        }? This action cannot be undone.`,
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Yes, Delete",
            style: "destructive",
            onPress: () => confirmDelete(lastIndexShipment),
          },
        ],
      );
    }
  };

  const confirmDelete = async (row: any) => {
    await dispatch(deleteFactoryRunShipment(row?._id))
      .unwrap()
      .then(() => {
        dispatch(getChinaTrackerShipmentDetailsByDetailId(order?._id)).unwrap();
        toast.show("Shipment deleted successfully", { type: "success" });
      })
      .catch((err: any) => {
        console.error(err, "error");
        toast.show(err || "Delete failed", {
          type: "danger",
        });
      });
  };

  const [shipmentEditModalOpen, setShipmentModalOpen] = useState<boolean>();
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const onShipmentEditModalClose = () => {
    setShipmentModalOpen(false);
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "shipment_update") {
      setShipmentModalOpen(true);
      setSelectedItem(order);
    }
  };

  return (
    <>
      {modalOpen && order && (
        <FactoryRunShipmentModal
          factoryOrderDetail={order}
          onClose={onCloseEnquiry}
          visible={modalOpen}
        />
      )}

      {shipmentEditModalOpen &&
        selectedItem &&
        prefixRow?.OrderStatus !== "Cancelled" && (
          <ChinaTrackerShipmentModalUpdate
            shipmentRow={selectedItem}
            onClose={onShipmentEditModalClose}
            visible={shipmentEditModalOpen}
            factoryOrderDetail={order}
          />
        )}

      <Modal visible={visible} transparent animationType="slide">
        <View style={styles.overlay}>
          <View
            style={
              isLargeDevice
                ? styles.modalContainer
                : styles.modalContainerSecond
            }
          >
            <View style={orderHeaderStyles.wrapper}>
              {/* Top Row: Order Number + Buttons */}
              <View style={orderHeaderStyles.topRow}>
                <Text selectable={true} style={orderHeaderStyles.orderLabel}>
                  {t("orderNumber")} - {order?.orderNumber}
                </Text>

                <View style={orderHeaderStyles.actionsRow}>
                  {prefixRow?.OrderStatus !== "Cancelled" && (
                    <TouchableOpacity
                      style={[
                        orderHeaderStyles.actionBtn,
                        orderHeaderStyles.addBtn,
                      ]}
                      onPress={() => setModalOpen(true)}
                    >
                      <Text style={orderHeaderStyles.actionText}>
                        {t("add")}
                      </Text>
                    </TouchableOpacity>
                  )}

                  {prefixRow?.OrderStatus !== "Cancelled" && (
                    <TouchableOpacity
                      style={[
                        orderHeaderStyles.actionBtn,
                        orderHeaderStyles.deleteBtn,
                      ]}
                      disabled={shipmentRows.length > 0 ? false : true}
                      onPress={handleDeletePress}
                    >
                      <Text style={orderHeaderStyles.actionText}>
                        {t("delete")}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>

              {/* Second Line: Run Info */}
              <Text selectable={true} style={orderHeaderStyles.runLabel}>
                {t("runOrder")} {order?.runOrder}
              </Text>
            </View>

            <ScrollView onScroll={handleScroll} ref={listRef}>
              <View>
                <View>
                  <View
                    style={[
                      styles.containerContent,
                      {
                        width: isLargeDevice
                          ? "100%"
                          : isMediumDevice
                            ? "100%"
                            : "100%",
                      },
                    ]}
                  >
                    {!loadingShipments && !loadingShipmentsAction ? (
                      <View style={{ padding: 2 }}>
                        <ChinaTrackerDataTableWithPagination2
                          headers={headers}
                          rows={shipmentRows}
                          itemsPerPage={10}
                          isDisplayTimeWithDate={true}
                          useModal={true}
                          isDetail={false}
                          isEdit={false}
                          isDelete={false}
                          // Required props
                          setModalVisible={() => { }}
                          handleRowClick={() => { }}
                          setSelected={() => { }}
                          handleDeleteRow={() => { }}
                          operationActions={navigateToOrder}
                          t={t}
                          page={page}
                          setPage={setPage}
                          selectable={true}
                        />
                      </View>
                    ) : (
                      <View>
                        <Spinner
                          size="small"
                          color="#74BBFF"
                          styles={{ flexDirection: "row", margin: "20%" }}
                        />
                      </View>
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
            <View style={styles.footer}>
              <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                <Text>{t("close")}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 25,
    width: "76%",
    borderRadius: 10,
  },
  title: {
    fontSize: 25,
    fontWeight: "bold",
    marginBottom: 35,
  },
  detailsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
  },
  column: {
    flex: 1,
  },
  label: {
    fontWeight: "bold",
    fontSize: 16,
    marginBottom: 3,
  },
  value: {
    fontSize: 16,
    marginBottom: 10,
    paddingLeft: 4,
  },
  dropdownRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  dropdownContainer: {
    flex: 1,
    marginRight: 10,
  },
  inputLabel: {
    fontWeight: "bold",
    marginBottom: 5,
    marginTop: 20,
  },
  picker: {
    borderWidth: 1.5,
    borderColor: "#a6a6a6",
    height: 40,
    backgroundColor: "#fff",
    borderRadius: 10,
  },
  messageInput: {
    height: 100,
    borderWidth: 1.5,
    borderColor: "#a6a6a6",
    padding: 10,
    backgroundColor: "#fff",
    marginBottom: 15,
    borderRadius: 10,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  userName: {
    fontSize: 14,
  },
  submitButton: {
    backgroundColor: "#000",
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 5,
  },
  submitText: {
    color: "#fff",
    fontWeight: "bold",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
  },
  buttonPrimary: { padding: 10, backgroundColor: "black", borderRadius: 5 },
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    flexGrow: 1,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
  },

  fab: {
    bottom: -10,
    position: "absolute",
    zIndex: 1,
  },
  tabsContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    marginBottom: 10,
  },
  tab: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  activeTab: {
    borderBottomColor: "#3498db",
  },
  tabText: {
    color: "#999",
  },
  activeTabText: {
    color: "#3498db",
    fontWeight: "600",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 8,
    height: "100%",
    width: "100%",
    padding: 10,
  },
});

const orderHeaderStyles = StyleSheet.create({
  wrapper: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  orderLabel: {
    fontSize: 20,
    fontWeight: "700",
  },
  actionsRow: {
    flexDirection: "row",
    columnGap: 10, // spacing between buttons
  },
  actionBtn: {
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  addBtn: {
    backgroundColor: "#42A5F5", // light blue
  },
  deleteBtn: {
    backgroundColor: "#E53935", // red
  },
  actionText: {
    color: "#fff",
    fontWeight: "600",
  },
  runLabel: {
    marginTop: 8,
    fontSize: 16,
    fontWeight: "700",
  },
});

export default ChinaTrackerShipmentListModal;
